import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  useTheme
} from '@material-ui/core';

const useStyle = makeStyles((theme) => ({
  root: {
    width: '100%',
    position: 'relative',
    paddingTop: '70%'
  },
  window: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    backgroundColor: '#ffffff',
    borderRadius: '4% / 5%',
    boxShadow: '0 3px 20px rgba(0,0,0,0.3)',
    overflow: 'hidden'
  },
  titleBar: {
    display: 'flex',
    width: '100%',
    height: '10%',
    backgroundColor: theme.palette.primary.main,
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 4.75%',

    '& .button-circle': {
      display: 'block',
      width: '3.25%',
      position: 'relative',
      marginLeft: '2.25%',
      backgroundColor: 'white',
      borderRadius: '100% / 100%',

      '&:before': {
        content: '""',
        display: 'block',
        paddingTop: '100%'
      }
    }
  },
  viewPort: {
    width: '100%',
    height: '90%',
    padding: '4.75% 0 2.38% 0',
    whiteSpace: 'nowrap',

    '& > div': {
      display: 'inline-block',
      height: '100%',

      '& > div:nth-child(1)': {
        width: '100%',
        height: '83.2%',
      },

      '& > div:nth-child(2)': {
        width: '100%',
        height: '4.25%',
      },

      '& > div:nth-child(3)': {
        width: '100%',
        height: '12.55%',
        display: 'flex',
        justifyContent: 'flex-end'
      }
    },

    '& .editor-column': {
      width: '100%',
      padding: '0 3.9%',
      transition: 'width 150ms ease-in-out',

      '& .editor-box': {
        borderRadius: '3% / 4%',
        backgroundColor: '#F2F2F2',
        width: '100%',
        border: '2px solid rgba(0, 108, 255, 0.43)',
        padding: '3% 5%',
        fontSize: 'calc(0.5em + 1vmin)',
        fontWeight: 'bold',
        overflow: 'hidden'
      }
    },
    '&.active .editor-column': {
      width: '65%',
    },

    '& .output-column': {
      width: '35%',
      paddingRight: '3.9%',

      '& .output-items-container': {
        display: 'flex',
        flexDirection: 'column',

        '& > div': {
          flex: '1 0 0',
          margin: '1.25% 0',
          '&:first-child': {
            marginTop: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          },
          '&:last-child': {
            marginBottom: 0
          }
        }
      }
    }
  },
  coloredBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'calc(1vmin + 1.2%)',
    fontSize: 'calc(1vmin + 0.4em)',
    fontWeight: 'bold',
    height: '100%',
    minHeight: 0,
    padding: '0 7%',
    color: theme.palette.primary.contrastText,
  }
}));

const CheckerDemo = () => {
  const classes = useStyle();
  const theme = useTheme();
  const [active, setActive] = useState<boolean>(false);

  useEffect(() => {
    const timeout = setTimeout(() => setActive(true), 1500);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.window}>
        <div className={classes.titleBar}>
          <span className="button-circle" style={{backgroundColor: theme.palette.warning.main}} />
          <span className="button-circle" style={{backgroundColor: theme.palette.success.main}} />
          <span className="button-circle" style={{backgroundColor: theme.palette.error.main}} />
        </div>
        <div className={classes.viewPort + ` ${active ? 'active' : ''}`}>
          <div className="editor-column">
            <div className="editor-box">
              <span style={{color: theme.palette.primary.main}}>function </span>
              <span style={{color: theme.palette.error.main}}>main</span>
              <span style={{color: theme.palette.text.disabled}}> (</span>
              <span style={{color: theme.palette.text.secondary}}>x, y</span>
              <span style={{color: theme.palette.text.disabled}}>) {'{'}</span>
              <span><br />&nbsp;&nbsp;&nbsp;&nbsp;</span>
              <span style={{color: theme.palette.text.secondary}}>console.</span>
              <span style={{color: theme.palette.error.main}}>log</span>
              <span style={{color: theme.palette.text.disabled}}>(</span>
              <span style={{color: theme.palette.success.main}}>"Total: "</span>
              <span style={{color: theme.palette.text.disabled}}> + (</span>
              <span style={{color: theme.palette.text.secondary}}>x</span>
              <span style={{color: theme.palette.text.disabled}}> +</span>
              <span style={{color: theme.palette.text.secondary}}> y</span>
              <span style={{color: theme.palette.text.disabled}}>))</span>
              <span><br /></span>
              <span style={{color: theme.palette.text.disabled}}>{'}'}</span>
            </div>
            <div>{/*spacer*/}</div>
            <div>
              <div className={classes.coloredBox} style={{backgroundColor: theme.palette.primary.main}}>
                RUN TEST
              </div>
            </div>
          </div>
          <div className="output-column">
            <div className="output-items-container">
              <div className={classes.coloredBox} style={{backgroundColor: theme.palette.warning.main}}>
                TEST OUTPUT
              </div>
              <div className={classes.coloredBox} style={{backgroundColor: theme.palette.success.main}} />
              <div className={classes.coloredBox} style={{backgroundColor: theme.palette.error.main}} />
              <div className={classes.coloredBox} style={{backgroundColor: theme.palette.success.main}} />
              <div className={classes.coloredBox} style={{backgroundColor: theme.palette.error.main}} />
              <div className={classes.coloredBox} style={{backgroundColor: theme.palette.success.main}} />
            </div>
            <div>{/*spacer*/}</div>
            <div>
              <div className={classes.coloredBox} style={{backgroundColor: theme.palette.primary.main}}>
                SUBMIT
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckerDemo;
